import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, User, MapPin, CheckCircle, LogOut, Radio } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import {
  getAllLoneWorkers,
  createLoneWorkerSession,
  loneWorkerCheckIn,
  loneWorkerCheckOut,
  LoneWorkerSession,
} from "../../../service/securityService";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

type ZoneOpt = { value: string; label: string };

const LoneWorkerTab: React.FC<{ zones: ZoneOpt[] }> = ({ zones }) => {
  const [rows, setRows] = useState<LoneWorkerSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    staffId: "",
    staffName: "",
    department: "",
    location: "",
    zone: "",
    taskDescription: "",
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllLoneWorkers({ page: 1, limit: 100 });
      setRows(res.data || []);
    } catch (e: any) {
      console.error(e);
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.resMessage ||
          "Failed to load lone worker sessions",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.staffName.trim()) {
      toast.error("Staff name is required");
      return;
    }
    try {
      await createLoneWorkerSession({
        staffId: form.staffId.trim() || form.staffName.trim(),
        staffName: form.staffName.trim(),
        department: form.department.trim() || undefined,
        location: form.location.trim() || "Unknown",
        zone: form.zone || undefined,
        taskDescription: form.taskDescription.trim() || undefined,
      });
      toast.success("Lone worker session started");
      setModalOpen(false);
      setForm({
        staffId: "",
        staffName: "",
        department: "",
        location: "",
        zone: "",
        taskDescription: "",
      });
      load();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.resMessage ||
          "Failed to create session",
      );
    }
  };

  const onCheckIn = useCallback(
    async (id: string) => {
      try {
        await loneWorkerCheckIn(id, {});
        toast.success("Welfare check-in recorded");
        load();
      } catch (err: any) {
        toast.error(
          err.response?.data?.message ||
            err.response?.data?.resMessage ||
            "Check-in failed",
        );
      }
    },
    [load],
  );

  const onCheckOut = useCallback(
    async (id: string) => {
      try {
        await loneWorkerCheckOut(id);
        toast.success("Session completed");
        load();
      } catch (err: any) {
        toast.error(
          err.response?.data?.message ||
            err.response?.data?.resMessage ||
            "Check-out failed",
        );
      }
    },
    [load],
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: "#fff",
        foregroundColor: "#2a2a2a",
        headerTextColor: "#2a2a2a",
        headerBackgroundColor: "#E9F5FF",
        oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
        headerColumnResizeHandleColor: "rgb(126, 46, 132)",
        fontSize: "0.75rem",
        headerFontWeight: "bold",
      }),
    [],
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "staffName",
        headerName: "Staff",
        flex: 1.2,
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "8px" },
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "8px" },
        cellRenderer: (p: any) => (
          <span className="flex items-center gap-1 text-sm">
            <MapPin className="w-3.5 h-3.5 text-gray-400" />
            {p.value || "—"}
          </span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "8px" },
        cellRenderer: (p: any) => {
          const s = p.value || "";
          const cls =
            s === "Overdue"
              ? "bg-red-100 text-red-800"
              : s === "Active"
                ? "bg-emerald-100 text-emerald-800"
                : s === "Completed"
                  ? "bg-gray-100 text-gray-700"
                  : "bg-slate-100 text-slate-700";
          return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
              {s || "—"}
            </span>
          );
        },
      },
      {
        field: "lastCheckInTime",
        headerName: "Last check-in",
        flex: 1.1,
        minWidth: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "8px" },
        valueGetter: (p) =>
          p.data?.lastCheckInTime
            ? new Date(p.data.lastCheckInTime).toLocaleString()
            : "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (p: any) => {
          const row = p.data as LoneWorkerSession;
          const id = row._id || row.id;
          if (!id) return null;
          const active = row.status === "Active" || row.status === "Overdue";
          return (
            <div className="flex gap-1 py-1">
              {active && (
                <button
                  type="button"
                  title="Welfare check-in"
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                  onClick={() => onCheckIn(String(id))}
                >
                  <CheckCircle className="w-4 h-4" />
                </button>
              )}
              {active && (
                <button
                  type="button"
                  title="End session"
                  className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                  onClick={() => onCheckOut(String(id))}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [onCheckIn, onCheckOut],
  );

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-between items-center gap-3 p-4 border-b">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#0fa9a6]" />
            Lone worker monitoring
          </h3>
          <p className="text-sm text-gray-600 max-w-2xl">
            Start a session when staff work alone; record welfare check-ins. Sessions
            overdue (~2h without check-in) surface as <strong>Overdue</strong> on
            refresh.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600"
        >
          <Plus className="w-5 h-5" />
          Start session
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center p-12">
            <SpinLoader />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <User className="w-14 h-14 mx-auto mb-3 text-gray-300" />
            <p>No lone worker sessions yet.</p>
          </div>
        ) : (
          <div className="ag-theme-alpine w-full min-h-[360px]">
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              theme={gridTheme}
              headerHeight={56}
              rowHeight={48}
              domLayout="autoHeight"
              pagination
              paginationPageSize={15}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-lg rounded-xl shadow-lg relative flex flex-col max-h-[85vh]"
      >
        <div className="pt-5 px-6 overflow-y-auto flex-1">
          <h2 className="text-lg font-semibold border-b pb-2 mb-4">
            Start lone worker session
          </h2>
          <form id="lwForm" onSubmit={handleCreate} className="space-y-4 pb-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Staff name *</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={form.staffName}
                onChange={(e) => setForm({ ...form, staffName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Staff / employee ID</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={form.staffId}
                onChange={(e) => setForm({ ...form, staffId: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Department</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Location</label>
              <input
                className="mt-1 w-full border rounded-md p-2"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Building / floor / room"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Zone</label>
              <select
                className="mt-1 w-full border rounded-md p-2"
                value={form.zone}
                onChange={(e) => setForm({ ...form, zone: e.target.value })}
              >
                <option value="">—</option>
                {zones.map((z) => (
                  <option key={z.value} value={z.value}>
                    {z.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Task</label>
              <textarea
                className="mt-1 w-full border rounded-md p-2 resize-y"
                rows={2}
                value={form.taskDescription}
                onChange={(e) =>
                  setForm({ ...form, taskDescription: e.target.value })
                }
              />
            </div>
          </form>
        </div>
        <div className="flex justify-end gap-2 p-3 bg-[#5A7192] rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="lwForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full"
          >
            Start
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LoneWorkerTab;
